import { Injectable } from '@angular/core';
import type { TreeEntity } from '../models/entity.model';
import type { EntityType } from '../models/entity-type.model';

@Injectable({
  providedIn: 'root'
})
export class EntityHelperService {
  /**
   * Recursively searches all entities with the matching key from the entity tree of the given entity. Note that given
   * entity is EXCLUDED from the result.
   * @param entity
   * @param entityTypeKey
   */
  public getEntitiesOfTypeFromEntityTree(entity: TreeEntity, entityTypeKey: string): TreeEntity[] {
    const nonMatchingEntities = entity.Children.filter(child => child.EntityTypeKey !== entityTypeKey);
    const matchingEntities = entity.Children.filter(child => child.EntityTypeKey === entityTypeKey);

    const entities = [...matchingEntities];
    nonMatchingEntities.forEach(c => {
      const results = this.getEntitiesOfTypeFromEntityTree(c, entityTypeKey);
      entities.push(...results);
    });
    return entities;
  }

  public entityTreeContainsMetaDataFlag(entity: TreeEntity, entityTypes: Record<string, EntityType>, flag: string): boolean {
    const entityType = entityTypes[entity.EntityTypeKey];
    if (!entityType) {
      return false;
    }
    const propertyTypes = entityType.Groups.flatMap(group => group.Properties);

    if (propertyTypes.some(propType => propType.MetaData[flag] && entity.Properties[propType.Key].Value)) {
      return true;
    }

    for (const child of entity.Children) {
      if (this.entityTreeContainsMetaDataFlag(child, entityTypes, flag)) {
        return true;
      }
    }

    return false;
  }

  public getEntityInTree(entity: TreeEntity, id: string): TreeEntity {
    const rootEntity = this.getRootEntity(entity);

    if (rootEntity.Id === id) {
      return rootEntity;
    }

    const findInChildren = (e: TreeEntity) => {
      const found = e.Children.find(child => child.Id === id);
      if (found) {
        return found;
      }

      for (const child of e.Children) {
        const foundInChild = findInChildren(child);
        if (foundInChild) {
          return foundInChild;
        }
      }

      return null;
    };

    return findInChildren(rootEntity);
  }

  public getRootEntity(entity: TreeEntity): TreeEntity {
    let currentEntity: TreeEntity = entity;
    while (currentEntity.Parent) {
      currentEntity = currentEntity.Parent;
    }
    return currentEntity;
  }

  public flattenEntity(entity: TreeEntity): TreeEntity[] {
    const result: TreeEntity[] = [];

    result.push(entity);

    for (const child of entity.Children) {
      result.push(...this.flattenEntity(child));
    }

    return result;
  }
}
